import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deactivateLinearCustomerMapping,
  getLinearCustomerMapping,
  getUnassignedLinearCustomers,
  reactivateLinearCustomerMapping,
  setLinearCustomerMapping,
} from '../services/linearCustomerMappingService';
import {
  DeactivateLinearCustomerMappingRequest,
  LinearCustomerMapping,
  LinearCustomerSummary,
} from '../models/linearCustomerMapping';

const mappingKey = (tenantId?: string) => ['linear-customer-mapping', tenantId];
const unassignedCustomersKey = ['linear-unassigned-customers'];

export const useLinearCustomerMapping = (tenantId?: string) =>
  useQuery<LinearCustomerMapping | null>({
    queryKey: mappingKey(tenantId),
    queryFn: () => getLinearCustomerMapping(tenantId!),
    enabled: !!tenantId,
  });

export const useUnassignedLinearCustomers = (enabled: boolean) =>
  useQuery<LinearCustomerSummary[]>({
    queryKey: unassignedCustomersKey,
    queryFn: getUnassignedLinearCustomers,
    enabled,
  });

export const useDeactivateSupportPortal = (tenantId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DeactivateLinearCustomerMappingRequest) =>
      deactivateLinearCustomerMapping(tenantId!, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mappingKey(tenantId) });
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
    },
  });
};

export const useReactivateSupportPortal = (tenantId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => reactivateLinearCustomerMapping(tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mappingKey(tenantId) });
    },
  });
};

export const useSetSupportPortalCustomer = (tenantId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linearCustomerId: string) =>
      setLinearCustomerMapping(tenantId!, { linearCustomerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mappingKey(tenantId) });
      queryClient.invalidateQueries({ queryKey: unassignedCustomersKey });
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
    },
  });
};
